import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";

import { getAnalyticsSummary } from "../../../../services/reportService";
import { modal, alert } from "../../../../js/modules/Dialogs";
import { useLoader } from "../../../../hooks/useLoader";
import { sessionExpired } from "../../../../config/messages";
import { toScreen } from "../../../../js/modules/Routing";
import withNavigate from "../../withNavigate";
import {
  CalendarDateIcon,
  CloseLineIcon,
  ResetIcon,
} from "../../../commonUI/Icons/SvgIcon";
import DatePicker from "react-datepicker";
import moment from "moment-timezone";

import OverviewSection from "./OverviewSection";
import WebsiteSection from "./WebsiteSection";
import AppSection from "./AppSection";
import TopNav from "../../../commonUI/TopHeader/TopHeader";

// ── Period buttons config ────────────────────────────────────────────────────
const periodButtons = [
  { id: "custom", label: "Custom range" },
  { id: "7D", label: "7D" },
  { id: "30D", label: "30D" },
  { id: "3M", label: "3M" },
  { id: "6M", label: "6M" },
  { id: "12M", label: "12M" },
];

// ── Main component ───────────────────────────────────────────────────────────
const RptAnalytics = ({ userDetails, navigate }) => {
  const { showLoader, hideLoader } = useLoader();
  const [period, setPeriod] = useState("weekly");
  const [snapshots, setSnapshots] = useState({});
  const [activeTab, setActiveTab] = useState("overview");
  const [cancelledStartDate, setCancelledStartDate] = useState(null);
  const [cancelledEndDate, setCancelledEndDate] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState("12M");

  // undefined = not yet loaded, null = API returned data:null, object = data
  const snapshot = snapshots[period];

  const handleAuthFailure = useCallback(() => {
    modal(sessionExpired);
    toScreen(false, navigate, "/logout");
  }, [navigate]);

  const load = useCallback(
    (which, silent) => {
      if (!silent) showLoader();
      getAnalyticsSummary(which)
        .then((data) => {
          if (!silent) hideLoader();
          setSnapshots((prev) => ({ ...prev, [which]: data }));
        })
        .catch((error) => {
          if (!silent) hideLoader();
          if (error.response?.status === 401) return handleAuthFailure();
          alert(error.message || "Failed to load analytics data");
        });
    },
    [showLoader, hideLoader, handleAuthFailure],
  );

  useEffect(() => {
    if (snapshots[period] === undefined) load(period);
  }, [period, snapshots, load]);

  // ── Content renderer ───────────────────────────────────────────────────────
  const renderContent = () => {
    // Empty state: API returned data: null
    if (snapshot === null) {
      return (
        <div className="c-report-format-label" style={{ marginTop: 24 }}>
          No report for this period yet. Google Analytics takes up to 48 hours
          to finish processing.
        </div>
      );
    }

    // Guard against missing data - still loading or error
    if (!snapshot || !snapshot.metrics || !snapshot.metrics.overview) {
      return (
        <div className="c-report-format-label" style={{ marginTop: 24 }}>
          {!snapshot
            ? "Loading analytics data..."
            : "Unable to load analytics metrics. Please try refreshing."}
        </div>
      );
    }

    return (
      <>
        <div className="analytics-dashboard">
          {/* Date Range Box */}
          <div className="analytics-date-range">
            <div className="analytics-date-range__label">Date range</div>

            <div className="analytics-controls">
              <div className="period-buttons-wrapper">
                <div className="custom-range-dropdown">
                  <button
                    className="period-btn custom-range-btn"
                    onClick={() => setShowDatePicker(!showDatePicker)}
                    style={{
                      padding:
                        cancelledStartDate && cancelledEndDate
                          ? "9px 32px 9px 12px"
                          : "9px 12px",
                    }}
                  >
                    <CalendarDateIcon width="16" height="16" />
                    {cancelledStartDate && cancelledEndDate
                      ? `${moment(cancelledStartDate).format("DD MMM YY")} - ${moment(cancelledEndDate).format("DD MMM YY")}`
                      : "Custom range"}
                    {cancelledStartDate && cancelledEndDate && (
                      <div
                        className="analytics-close-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCancelledStartDate(null);
                          setCancelledEndDate(null);
                        }}
                      >
                        <CloseLineIcon width="14" height="14" />
                      </div>
                    )}
                  </button>
                </div>
                <div className="period-buttons">
                  {periodButtons.slice(1).map((button) => (
                    <button
                      key={button.id}
                      className={`period-btn ${
                        selectedPeriod === button.id ? "active" : ""
                      }`}
                      onClick={() => {
                        setSelectedPeriod(button.id);
                        setShowDatePicker(false);
                      }}
                    >
                      {button.label}
                    </button>
                  ))}
                </div>
              </div>
              <button className="reset-button">
                <ResetIcon width="16" height="16" />
                Reset
              </button>
              {showDatePicker && (
                <div
                  className="date-picker-backdrop"
                  onClick={() => setShowDatePicker(false)}
                />
              )}
              {showDatePicker && (
                <div className="date-picker-dropdown">
                  <DatePicker
                    selectsRange
                    className="filter-box date-picker"
                    startDate={cancelledStartDate}
                    endDate={cancelledEndDate}
                    onChange={(dates) => {
                      const [start, end] = dates;
                      setCancelledStartDate(start);
                      setCancelledEndDate(end);
                      if (start && end) {
                        setShowDatePicker(false);
                      }
                    }}
                    placeholderText="Anytime"
                    monthsShown={2}
                    inline
                    showIcon={false}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="custom-tabs mt-0">
            {["Overview", "Website", "Apps"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab.toLowerCase())}
                className={`tab-item ${
                  activeTab === tab.toLowerCase() ? "active" : ""
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Tab Sections */}
          {activeTab === "overview" && <OverviewSection snapshot={snapshot} selectedPeriod={selectedPeriod} />}
          {activeTab === "website" && <WebsiteSection snapshot={snapshot} />}
          {activeTab === "apps" && <AppSection snapshot={snapshot} />}
        </div>
      </>
    );
  };

  return (
    <div className="l-base-layout__content">
      <TopNav title={"Analytics"}/>

      {/* Render content only once data has been attempted (not undefined) */}
      {snapshot !== undefined && renderContent()}
    </div>
  );
};

RptAnalytics.propTypes = {
  userDetails: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
};

export default withNavigate(RptAnalytics);
